/**
 * Logger utility for client-side logging
 * Handles development and production logging with appropriate levels
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';

  /**
   * Format log message with timestamp and level
   */
  private format(level: LogLevel, message: string, data?: any): string {
    const timestamp = new Date().toISOString();
    const dataStr = data ? ` ${JSON.stringify(data)}` : '';
    return `[${timestamp}] [${level.toUpperCase()}] ${message}${dataStr}`;
  }

  /**
   * Log debug messages (development only)
   */
  debug(message: string, data?: any): void {
    if (this.isDevelopment) {
      console.debug(this.format('debug', message, data));
    }
  }

  /**
   * Log info messages (development only)
   */
  info(message: string, data?: any): void {
    if (this.isDevelopment) {
      console.info(this.format('info', message, data));
    }
  }

  /**
   * Log warning messages
   */
  warn(message: string, data?: any): void {
    console.warn(this.format('warn', message, data));
  }

  /**
   * Log error messages with stack trace
   */
  error(message: string, error?: any, data?: any): void {
    const stack = error instanceof Error ? error.stack : undefined;
    const errorData = error instanceof Error ? { name: error.name, message: error.message } : error;
    const combinedData = data ? { ...errorData, ...data } : errorData;

    console.error(this.format('error', message, combinedData));
    if (stack) {
      console.error('Stack:', stack);
    }
  }

  /**
   * Log API calls (development only)
   */
  logApiCall(method: string, url: string, status?: number, duration?: number): void {
    if (!this.isDevelopment) return;
    const message = `API ${method} ${url}${status ? ` -> ${status}` : ''}`;
    const data = duration ? { duration: `${duration}ms` } : undefined;
    console.log(this.format('info', message, data));
  }

  /**
   * Log API errors
   */
  logApiError(method: string, url: string, error: any, status?: number): void {
    const message = `API ${method} ${url} failed`;
    const data = {
      status,
      message: error?.message,
      code: error?.code,
    };
    this.error(message, error, data);
  }

  /**
   * Log authentication events (development only)
   */
  logAuth(event: 'login' | 'logout' | 'register' | 'token_refresh', data?: any): void {
    if (this.isDevelopment) {
      this.info(`Auth: ${event}`, data);
    }
  }

  /**
   * Log user actions (development only)
   */
  logAction(action: string, data?: any): void {
    if (this.isDevelopment) {
      this.info(`Action: ${action}`, data);
    }
  }
}

export const logger = new Logger();
